import { useEffect, useState } from "react";
import { User, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUiTexts } from "@/lib/uiTexts";

const CATEGORIES = [
  { key: "lol", label: "League of Legends", testid: "roster-tab-lol" },
  { key: "tft", label: "Teamfight Tactics", testid: "roster-tab-tft" },
  { key: "r6", label: "Rainbow Six Siege", testid: "roster-tab-r6" },
  { key: "staff", label: "Staff", testid: "roster-tab-staff" },
];

function MemberCard({ m }) {
  const texts = useUiTexts();
  const operators = (m.operators || "").split(",").map((o) => o.trim()).filter(Boolean);
  const statsLabel = m.category === "r6" ? texts.roster_r6_label : texts.roster_opgg_label;
  return (
    <div data-testid={`roster-card-${m.nickname}`} className="card-tactical group overflow-hidden transition-transform hover:-translate-y-1">
      {m.image_url ? (
        <div className="h-64 bg-[#060B18]">
          <img src={m.image_url} alt={m.nickname} loading="lazy" className="h-full w-full object-contain" />
        </div>
      ) : (
        <div className="flex h-64 items-center justify-center bg-[#060B18]">
          <User className="h-14 w-14 text-orange-500/40" />
        </div>
      )}
      <div className="p-6">
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-orange-400">
            {m.role}
          </span>
          {m.rank && (
            <span data-testid={`roster-rank-${m.nickname}`} className="rounded-md border border-cyan-400/40 bg-cyan-400/10 px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-cyan-300">
              {m.rank}
            </span>
          )}
        </div>
        <h3 className="mt-3 text-2xl font-black uppercase tracking-tight">{m.nickname}</h3>
        {operators.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`roster-operators-${m.nickname}`}>
            {operators.map((op) => (
              <span key={op} className="rounded border border-slate-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                {op}
              </span>
            ))}
          </div>
        )}
        {m.detail && <p className="mt-3 border-t border-orange-500/10 pt-3 text-xs text-slate-500">{m.detail}</p>}
        {m.opgg_url && (
          <a href={m.opgg_url} target="_blank" rel="noreferrer" data-testid={`roster-opgg-${m.nickname}`}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-cyan-300 transition-colors hover:text-cyan-200">
            {statsLabel} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function Rosters() {
  const texts = useUiTexts();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/rosters").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="rosters-page" className="page-enter mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">{texts.rosters_label}</p>
      <h1 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">{texts.rosters_title}</h1>

      <Tabs defaultValue="lol" className="mt-10">
        <TabsList className="border border-orange-500/20 bg-[#0B132B]">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.key} value={c.key} data-testid={c.testid}
              className="font-heading uppercase tracking-wide data-[state=active]:bg-orange-500 data-[state=active]:text-[#060B18]">
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {CATEGORIES.map((c) => (
          <TabsContent key={c.key} value={c.key} className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {members.filter((m) => m.category === c.key).map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
